import React from 'react';
import { useTranslation } from 'react-i18next';
import { Collection } from '../../api/CollectionsAPI';
import { useFormValidation } from '../../hooks/useFormValidation';
// import { productTypeToIcon, productTypeToTranslationKey } from '../../shared/paymentStatusToTranslationKey';
import { isValidText } from '../../shared/validation';
import Divider from '../Divider/Divider';
// import { Icon } from '../Icon/Icon';
import InputShortText from "../InputShortText/InputShortText";
// import SelectInput from '../SelectInput/SelectInput';
// import SelectInputV2 from '../SelectInputV2/SelectInputV2';
import SmallCheckBox from '../SmallCheckBox/SmallCheckBox';
import TextArea from '../TextArea/TextArea';
import CONFIG from '../../config/config';

type Props = {
  collection: Collection | null,
  onCollectionForm: (value: Collection) => void,
  isDisabled?: boolean,
  actionButton?: JSX.Element
};

const getInitialState = (partner?: Collection | null) => {
  if (partner) {
    return {
      name: isValidText(partner.name, 2),
      shortName: isValidText(partner.name, 2),
    };
  }
};

function CollectionForm({ collection, onCollectionForm, isDisabled = false, actionButton }: Props) {

  const { t } = useTranslation();
  // const [productType] = useState("dresses");
  const initialState = getInitialState(collection);
  const requiredFields = ['name', 'shortName'];
  const { updateFieldValidation, isFormValid } = useFormValidation(requiredFields, initialState);

  const disabledActionButton = actionButton
    ? React.cloneElement(
        actionButton,
        {},
        React.Children.map(actionButton.props.children, (child) => {
          return child ? React.cloneElement(child, { isDisabled: !isFormValid() }) : null;
        })
      )
    : null;

  return (
    <div className="flex flex-col flex-wrap space-y-6">
      {/* Basic Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.collections.form.sections.basic_info")}</h3>
        <InputShortText
          label={"admin.collections.form.name"}
          validationFn={(value) => !!value && value.length > 2}
          onValidationChange={(isValid) => updateFieldValidation('name', isValid)}
          onChange={e => onCollectionForm({ ...collection, name: e.currentTarget.value } as Collection)}
          errorMessage={"errors.too_short"}
          defaultValue={collection?.name || ''}
          isDisabled={isDisabled}
          helperText={t("admin.collections.form.name.helper")!}
        />
        <InputShortText
          label={"admin.collections.form.short_name"}
          validationFn={(value) => !!value && value.length > 2}
          onChange={e => onCollectionForm({ ...collection, shortName: e.currentTarget.value } as Collection)}
          onValidationChange={(isValid) => updateFieldValidation('shortName', isValid)}
          errorMessage={"errors.too_short"}
          defaultValue={collection?.shortName || ''}
          isDisabled={isDisabled}
          helperText={t("admin.collections.form.short_name.helper")!}
        />
        {/* <SelectInputV2
          values={["dresses"].map(e => { return { value: e, readableValue: t(productTypeToTranslationKey(e)), icon: <>{productTypeToIcon(e)}</> } })}
          onChange={e => onCollectionForm({ ...collection, type: e } as Collection)}
          label={"admin.collections.form.type"}
          defaultValue={productType}
          isDisabled={isDisabled}
        /> */}
      </div>

      <Divider />

      {/* Visibility Settings Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.collections.form.sections.visibility")}</h3>
        <SmallCheckBox
          label={"admin.collections.form.public"}
          defaultValue={collection?.public}
          onChange={(e: any) => onCollectionForm({ ...collection, public: e.target.checked } as Collection)}
          isDisabled={isDisabled}
          helperText={t("admin.collections.form.public.helper")!}
        />
        <SmallCheckBox
          label={"admin.collections.form.shareable"}
          defaultValue={collection?.shareableByLink}
          onChange={(e: any) => onCollectionForm({ ...collection, shareableByLink: e.target.checked } as Collection)}
          isDisabled={isDisabled}
          helperText={t("admin.collections.form.shareable.helper")!}
        />
        <SmallCheckBox
          label={"admin.collections.form.public_prices"}
          defaultValue={collection?.pricesArePublic}
          onChange={(e: any) => onCollectionForm({ ...collection, pricesArePublic: e.target.checked } as Collection)}
          isDisabled={isDisabled}
          helperText={t("admin.collections.form.public_prices.helper")!}
        />
        <SmallCheckBox
          label={"admin.collections.form.accepts_b2b_orders"}
          defaultValue={collection?.acceptsB2BOrders === true}
          onChange={(e: any) => onCollectionForm({ ...collection, acceptsB2BOrders: e.target.checked } as Collection)}
          isDisabled={isDisabled}
          helperText={t("admin.collections.form.accepts_b2b_orders.helper")!}
        />
      </div>

      <Divider />

      {/* iPad App Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.collections.form.sections.ipad_app")}</h3>
        <SmallCheckBox
          label={"admin.collections.form.featured"}
          isDisabled={isDisabled}
          defaultValue={collection?.featured}
          onChange={(e: any) => onCollectionForm({ ...collection, featured: e.target.checked } as Collection)}
          helperText={t("admin.collections.form.featured.helper")!}
        />
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            {t("admin.collections.form.ipad_app.experimental")} {CONFIG.CONTACT_INFO.EMAIL}
          </p>
        </div>
      </div>

      <Divider />

      {/* Description Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t("admin.collections.form.sections.description")}</h3>
        <TextArea
          label={"admin.collections.form.description"}
          validationFn={(value) => !value || (!!value && isValidText(value, 0, 10000))} 
          onChange={e => onCollectionForm({ ...collection, description: e.currentTarget.value } as Collection)}
          errorMessage={"errors.too_long"}
          defaultValue={collection?.description}
          isDisabled={isDisabled}
          maxLength={10000}
          helperText={t("admin.collections.form.description.helper")!}
        />
      </div>
      { disabledActionButton }
    </div>
  );
}

export default CollectionForm;
